import React, { useState } from 'react';
import { useLanguage } from '../components/LanguageContext';
import { PRICING_PLANS, USD_TO_NIS } from '../components/data/characters';
import { motion } from 'framer-motion';
import { 
  Check, 
  Sparkles, 
  Zap, 
  Crown,
  Gift,
  CreditCard,
  Minus,
  Plus,
  Shield,
  Star,
  Rocket,
  TrendingUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import {
  Card,
  CardContent,
  CardHeader,
} from "../components/ui/card";
import PaymentModal from '../components/payment/PaymentModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '../api/base44Client';

export default function Pricing() {
  const { t, language, isRTL } = useLanguage();
  // Currency display: NIS for Hebrew, USD (converted) for English.
  // The actual charge is always in NIS (gateway is Israeli).
  const showUsd = language !== 'he';
  const currencySymbol = showUsd ? '$' : '₪';
  const toDisplayPrice = (priceNis) => showUsd ? priceNis / USD_TO_NIS : priceNis;
  const fmt = (priceNis, digits = 2) => `${currencySymbol}${toDisplayPrice(priceNis).toFixed(digits)}`;
  const [customCredits, setCustomCredits] = useState(50);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const handlePurchaseClick = (plan) => {
    setSelectedPlan(plan);
    setIsPaymentModalOpen(true);
  };

  const handlePaymentSuccess = (result) => {
    // Refresh user data to show updated credits
    queryClient.invalidateQueries(['currentUser']);
    setIsPaymentModalOpen(false);
    setSelectedPlan(null);
  };

  const getCustomPrice = (credits) => {
    const starter = PRICING_PLANS.find((p) => p.id === 'starter');
    const boost = PRICING_PLANS.find((p) => p.id === 'boost');
    const pro = PRICING_PLANS.find((p) => p.id === 'pro');

    const starterPpc = starter?.pricePerCredit ?? 0.56;
    const boostPpc = boost?.pricePerCredit ?? 0.45;
    const proPpc = pro?.pricePerCredit ?? 0.37;

    // Piecewise curve, anchored to package pricing — each bucket sits between two packages:
    // <80    : starter price + 5%
    // 80-225 : boost price + 5%
    // 225-700: pro price + 3%
    // >700   : smoothly approaches enterprise price, but never below it
    let ppc;
    if (credits < 80) {
      ppc = starterPpc * 1.05;
    } else if (credits < 225) {
      ppc = boostPpc * 1.05;
    } else if (credits < 700) {
      ppc = proPpc * 1.03;
    } else {
      const business = PRICING_PLANS.find((p) => p.id === 'business');
      const enterprise = PRICING_PLANS.find((p) => p.id === 'enterprise');
      const businessPpc = business?.pricePerCredit ?? 0.30;
      const enterprisePpc = enterprise?.pricePerCredit ?? 0.26;
      // Interpolate from business → enterprise between 700 and 2500, then stay flat
      if (credits <= 2500) {
        const t = (credits - 700) / (2500 - 700);
        ppc = businessPpc + (enterprisePpc - businessPpc) * t;
      } else {
        // Above 2500 — small additional discount, but never below 80% of enterprise
        const t = Math.min(1, (credits - 2500) / 17500);
        ppc = enterprisePpc - (enterprisePpc * 0.2) * t;
      }
      ppc = Math.max(enterprisePpc * 0.8, ppc);
    }

    return (credits * ppc).toFixed(2);
  };

  const features = [
    { key: 'allCharacters', icon: Sparkles },
    { key: 'noWatermark', icon: Check },
    { key: 'hdQuality', icon: Check },
    { key: 'instantDelivery', icon: Zap },
  ];

  const proFeatures = [
    { key: 'commercialLicense', icon: Crown },
    { key: 'prioritySupport', icon: Crown },
    { key: 'customStyles', icon: Crown },
  ];

  const planIcons = {
    starter: Rocket,
    boost: Zap,
    pro: Crown,
    business: TrendingUp,
    enterprise: Shield,
  };

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t('pricingTitle')}
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            {language === 'he'
              ? 'רכוש קרדיטים ללא התחייבות • מחירים החל מ-₪0.26 לתמונה • ללא תאריך תפוגה'
              : 'Buy credits without commitment • Prices from $0.07 per photo • No expiration'
            }
          </p>
        </motion.div>

        {/* Credits & quality explanation */}
        <div className="max-w-3xl mx-auto mb-10 bg-slate-900/70 border border-slate-800 rounded-2xl p-4 text-sm text-slate-300 text-start">
          {language === 'he' ? (
            <>
              <p className="font-semibold mb-2">איך הקרדיטים עובדים בעמדת הצילום?</p>
              <p className="mb-1">• יצירה באיכות רגילה (HD) עולה <span className="font-semibold">קרדיט אחד</span>.</p>
              <p className="mb-1">• יצירה באיכות <span className="font-semibold">4K Ultra</span> עולה <span className="font-semibold">2 קרדיטים</span>.</p>
              <p>• אפשר להשתמש בקרדיטים על כל דמות, בכל אירוע, ללא תאריך תפוגה.</p>
            </>
          ) : (
            <>
              <p className="font-semibold mb-2">How do credits work in the photo booth?</p>
              <p className="mb-1">• A <span className="font-semibold">Standard (HD)</span> generation costs <span className="font-semibold">1 credit</span>.</p>
              <p className="mb-1">• A <span className="font-semibold">4K Ultra</span> generation costs <span className="font-semibold">2 credits</span>.</p>
              <p>• You can use credits on any character, at any event, and they never expire.</p>
            </>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="mb-12">
          <div className="md:hidden text-center text-xs text-slate-500 mb-3">
            {language === 'he' ? 'גלול ימינה/שמאלה כדי לראות את כל החבילות' : 'Swipe left/right to see all plans'}
          </div>
          <div
            className="-mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto md:overflow-visible pb-2"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <div className="flex md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 snap-x snap-mandatory md:snap-none">
          {PRICING_PLANS.map((plan, idx) => {
            const Icon = planIcons[plan.id] || CreditCard;
            
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`relative w-[85vw] max-w-sm flex-none md:w-auto snap-center ${plan.popular ? 'md:-mt-4 md:mb-4' : ''}`}
              >
                <Card className={`relative h-full overflow-hidden transition-all duration-300 ${
                  plan.popular 
                    ? 'bg-slate-900 border-violet-500/50 ring-2 ring-violet-500/30' 
                    : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'
                }`}>
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-violet-600 py-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Star className="w-4 h-4 text-white fill-white" />
                        <span className="text-white text-sm font-semibold">
                          {t('mostPopular')}
                        </span>
                        <Star className="w-4 h-4 text-white fill-white" />
                      </div>
                    </div>
                  )}
                  
                  <CardHeader className={`text-center ${plan.popular ? 'pt-14' : 'pt-6'}`}>
                    {/* Icon */}
                    <div className={`w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center ${
                      plan.id === 'starter' ? 'bg-blue-600/20' :
                      plan.id === 'boost' ? 'bg-violet-600/20' :
                      plan.id === 'pro' ? 'bg-amber-600/20' :
                      plan.id === 'business' ? 'bg-emerald-600/20' : 'bg-rose-600/20'
                    }`}>
                      <Icon className={`w-7 h-7 ${
                        plan.id === 'starter' ? 'text-blue-400' :
                        plan.id === 'boost' ? 'text-violet-400' :
                        plan.id === 'pro' ? 'text-amber-400' :
                        plan.id === 'business' ? 'text-emerald-400' : 'text-rose-400'
                      }`} />
                    </div>
                    
                    {/* Plan Name */}
                    <h3 className="text-xl font-bold text-white mb-1">
                      {language === 'he' ? plan.name_he : plan.name_en}
                    </h3>
                    
                    {/* Description */}
                    <p className="text-sm text-slate-400 mb-2">
                      {language === 'he' ? plan.description_he : plan.description_en}
                    </p>
                    
                    {/* Savings */}
                    {plan.savings && (
                      <span className="inline-block px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                        {t('save')} {plan.savings}%
                      </span>
                    )}
                    
                    {/* Price */}
                    <div className="mt-4">
                      <div className="flex items-baseline justify-center gap-1" dir="ltr">
                        <span className="text-lg text-slate-500">{currencySymbol}</span>
                        <span className="text-4xl font-bold text-white">{toDisplayPrice(plan.price).toFixed(0)}</span>
                        <span className="text-xl text-slate-500">.{(toDisplayPrice(plan.price) % 1).toFixed(2).split('.')[1]}</span>
                      </div>
                      <p className="text-sm text-slate-500 mt-1" dir="ltr">
                        {fmt(plan.pricePerCredit, 2)} {t('perCredit')}
                      </p>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0 pb-6">
                    {/* Credits Display */}
                    <div className="bg-slate-800/50 rounded-xl p-4 mb-6 text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <span className="text-3xl font-bold text-white">{plan.credits}</span>
                        <span className="text-slate-400">{t('credits')}</span>
                      </div>
                      
                      {plan.bonus > 0 && (
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 mb-2">
                          <Gift className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 font-semibold text-sm">+{plan.bonus} {t('bonus')}</span>
                        </div>
                      )}
                      
                      <div className="flex items-center justify-center gap-2 pt-2 border-t border-slate-700/50 mt-2">
                        <span className="text-slate-500">=</span>
                        <span className="text-xl font-bold text-violet-400">{plan.totalCredits}</span>
                        <span className="text-slate-500 text-sm">{language === 'he' ? 'סה״כ' : 'total'}</span>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-2.5 mb-6">
                      {features.map((feature) => (
                        <li key={feature.key} className="flex items-center gap-3">
                          <Check className="w-4 h-4 text-slate-500" />
                          <span className="text-slate-300 text-sm">{t(feature.key)}</span>
                        </li>
                      ))}
                      {(plan.id === 'pro' || plan.id === 'business' || plan.id === 'enterprise') && proFeatures.map((feature) => (
                        <li key={feature.key} className="flex items-center gap-3">
                          <Crown className="w-4 h-4 text-amber-400" />
                          <span className="text-amber-300 text-sm">{t(feature.key)}</span>
                        </li>
                      ))}
                      {plan.id === 'enterprise' && (
                        <li className="flex items-center gap-3">
                          <Shield className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-300 text-sm">{language === 'he' ? 'תמיכה עדיפה 24/7' : '24/7 Priority Support'}</span>
                        </li>
                      )}
                    </ul>

                    {/* CTA Button */}
                    <Button 
                      onClick={() => handlePurchaseClick(plan)}
                      className={`w-full h-12 text-sm font-medium rounded-xl transition-all ${
                        plan.popular
                          ? 'bg-violet-600 hover:bg-violet-700 text-white'
                          : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                      }`}
                    >
                      <CreditCard className="w-4 h-4 me-2" />
                      {t('buyNow')}
                    </Button>

                    {/* Security */}
                    <div className="flex items-center justify-center gap-2 mt-4 text-slate-600 text-xs">
                      <Shield className="w-3 h-3" />
                      {language === 'he' ? 'תשלום מאובטח' : 'Secure Payment'}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
            </div>
          </div>
        </div>

        {/* Custom Pack */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-gradient-to-br from-slate-900 to-slate-900/80 border-slate-700 overflow-hidden">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-6 items-stretch">
                <div className="bg-slate-800/40 rounded-2xl p-6 border border-slate-700 h-full">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/20 text-violet-400 text-sm font-medium mb-4">
                    <Sparkles className="w-4 h-4" />
                    {language === 'he' ? 'התאם אישית' : 'Customize'}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {language === 'he' ? 'חבילה מותאמת אישית' : 'Custom Credit Pack'}
                  </h3>
                  <p className="text-slate-400 mb-6">
                    {language === 'he' 
                      ? 'בחר בדיוק כמה קרדיטים אתה צריך - ככל שקונים יותר, המחיר לקרדיט יורד!'
                      : 'Choose exactly how many credits you need - the more you buy, the lower the price per credit!'}
                  </p>

                  <div className="space-y-4">
                    {/* Credits Input with +/- buttons */}
                    <div className="flex items-center justify-center gap-3 bg-slate-800/70 rounded-xl p-4">
                      <Button
                        onClick={() => setCustomCredits(Math.max(25, customCredits - 25))}
                        className="w-12 h-12 rounded-full bg-slate-700 hover:bg-slate-600 text-white text-xl font-bold"
                      >
                        −
                      </Button>
                      <div className="flex-1 text-center">
                        <input
                          type="number"
                          value={customCredits}
                          onChange={(e) => setCustomCredits(Math.min(20000, Math.max(25, parseInt(e.target.value) || 25)))}
                          className="w-32 bg-transparent text-center text-3xl font-bold text-white outline-none"
                        />
                        <div className="text-slate-500 text-sm">{t('credits')}</div>
                      </div>
                      <Button
                        onClick={() => setCustomCredits(Math.min(20000, customCredits + 25))}
                        className="w-12 h-12 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-xl font-bold"
                      >
                        +
                      </Button>
                    </div>

                    {/* Slider */}
                    <div className="px-2">
                      <Slider
                        value={[customCredits]}
                        onValueChange={([val]) => setCustomCredits(val)}
                        min={25}
                        max={20000}
                        step={25}
                        className="py-4"
                      />
                      <div className="flex items-center justify-between text-slate-500 text-xs mt-1">
                        <span>10 {language === 'he' ? 'קרדיטים' : 'credits'}</span>
                        <span>2000 {language === 'he' ? 'קרדיטים' : 'credits'}</span>
                      </div>
                    </div>

                    {/* Quick Select Buttons */}
                    <div className="flex flex-wrap gap-2 justify-center">
                      {[50, 100, 200, 500, 1000, 2000].map((amount) => (
                        <button
                          key={amount}
                          onClick={() => setCustomCredits(amount)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            customCredits === amount
                              ? 'bg-violet-600 text-white'
                              : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                          }`}
                        >
                          {amount}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800/80 rounded-2xl p-6 text-center border border-slate-700 h-full flex flex-col justify-center">
                  {(() => {
                    const total = parseFloat(getCustomPrice(customCredits));
                    const ppc = total / customCredits;
                    return (
                      <>
                  <div className="text-sm text-slate-400 mb-2">{language === 'he' ? 'סה״כ לתשלום' : 'Total Price'}</div>
                  <div className="text-5xl font-bold text-white mb-1" dir="ltr">
                    {fmt(total, 2)}
                  </div>

                  <div className="mb-3" dir="ltr">
                    <div className="text-sm text-slate-500">
                      {fmt(ppc, 3)} {t('perCredit')}
                    </div>
                    <div className="text-xs text-slate-500">
                      {language === 'he' ? 'מגיע עד ₪0.26 לקרדיט בכמויות גדולות' : 'Reaches down to $0.07/credit at high volumes'}
                    </div>
                  </div>
                  
                  {ppc <= 0.07 && (
                    <div className="mb-4 px-4 py-2 rounded-full bg-green-500/20 text-green-400 text-sm font-semibold inline-flex items-center gap-2">
                      <Star className="w-4 h-4 fill-green-400" />
                      {language === 'he' ? 'המחיר הטוב ביותר!' : 'Best Price!'}
                    </div>
                  )}
                  {customCredits >= 200 && ppc > 0.07 && (
                    <div className="mb-4 px-4 py-2 rounded-full bg-amber-500/20 text-amber-400 text-sm font-semibold inline-block">
                      {language === 'he' ? 'חיסכון גדול!' : 'Great Savings!'}
                    </div>
                  )}

                  <Button 
                    onClick={() => handlePurchaseClick({
                      name: language === 'he' ? 'חבילה מותאמת אישית' : 'Custom Pack',
                      price: total,
                      credits: customCredits
                    })}
                    className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white h-14 text-lg font-semibold rounded-xl"
                  >
                    <CreditCard className="w-5 h-5 me-2" />
                    {t('buyNow')}
                  </Button>
                  
                  <div className="flex items-center justify-center gap-2 mt-4 text-slate-500 text-xs">
                    <Shield className="w-3 h-3" />
                    {language === 'he' ? 'תשלום מאובטח' : 'Secure Payment'}
                  </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            {language === 'he' ? 'שאלות נפוצות' : 'Frequently Asked Questions'}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[ 
              {
                q: language === 'he' ? 'כמה עולה כל יצירה?' : 'How much does each generation cost?',
                a: language === 'he'
                  ? 'יצירה באיכות רגילה (HD) עולה קרדיט אחד, ויצירה באיכות 4K Ultra עולה 2 קרדיטים.'
                  : 'A Standard (HD) generation costs 1 credit, and a 4K Ultra generation costs 2 credits.'
              },
              {
                q: language === 'he' ? 'האם הקרדיטים פגים?' : 'Do credits expire?',
                a: language === 'he' ? 'לא, הקרדיטים שלך לעולם לא פגים.' : 'No, your credits never expire.'
              },
              {
                q: language === 'he' ? 'האם אוכל לקבל החזר?' : 'Can I get a refund?',
                a: language === 'he' ? 'כן, אנו מציעים החזר מלא תוך 7 ימים אם לא השתמשת בקרדיטים.' : 'Yes, we offer a full refund within 7 days if you haven\'t used any credits.'
              },
              {
                q: language === 'he' ? 'מה זה רישיון מסחרי?' : 'What is a commercial license?',
                a: language === 'he' ? 'רישיון מסחרי מאפשר לך להשתמש בתמונות למטרות עסקיות.' : 'A commercial license allows you to use images for business purposes.'
              },
            ].map((faq, idx) => (
              <div key={idx} className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-slate-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      
      {/* Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        selectedPlan={selectedPlan}
        onSuccess={handlePaymentSuccess}
      />
    </div>
  );
}
